import { Space, Typography } from "antd";
import { Toggle } from "@libertify/ui";
import { useTranslate } from "@refinedev/core";
import { useMemo } from "react";
import { PanelSection } from "../../storyboardEditor/components/StoryboardMenuTabs/PanelSection";
import { useTemplateEditorStore } from "../../storyboardEditor/store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../storyboardEditor/store/useTemplatesStatusStore";
import { ItemAlignmentFeature } from "../../storyboardEditor/components/features/ItemAlignmentFeature";
import { TextStyleFeature } from "../../storyboardEditor/components/StoryboardMenuTabs/TextTab/features/TextStyleFeature";
import { SettingSlider } from "../../storyboardEditor/components/controls/SettingSlider";
import { DEFAULT_SUBTITLES_LINE_HEIGHT } from "../../storyboardEditor/constants";
import { ParameteredText } from "../../storyboardEditor/types";
import { ITEM_ID_TO_INPUT_KEY } from "../helpers";

export const ParameteredTextTab = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();

  const {
    currentItem,
    parameteredTexts,
    updateParameteredText,
    figure,
    updateFigure,
  } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
    parameteredTexts: store.templateState.parameteredTexts,
    updateParameteredText: store.updateParameteredText,
    figure: store.templateState.figure,
    updateFigure: store.updateFigure,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const currentParameteredText = parameteredTexts?.find(
    (text) => text.id === currentItem?.id
  );
  const isParameteredText = !!currentParameteredText;
  const isFigureNumber = figure?.number.id === currentItem?.id;
  const isFigureUnits = figure?.units?.id === currentItem?.id;

  const currentTextItem =
    currentParameteredText ??
    (isFigureNumber ? figure?.number : undefined) ??
    (isFigureUnits ? figure?.units : undefined);

  const updateText = (value: Partial<ParameteredText>) => {
    if (isParameteredText) {
      updateParameteredText(value);
    }
    if (isFigureNumber) {
      updateFigure(value, "number");
    }
    if (isFigureUnits) {
      updateFigure(value, "units");
    }
  };

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.textTab.title")}
        subtitle={t("components.templates.settings.textTab.description")}
        primary
      />
      {(parameteredTexts || figure) && (
        <PanelSection
          title={t("components.templates.settings.textTab.displayOption")}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {parameteredTexts?.map((text) => (
              <Space key={text.id} align="center">
                <Toggle
                  checked={text.visible}
                  onChange={(checked) => {
                    updateParameteredText({ visible: checked, id: text.id });
                    addTemplateUpdate();
                  }}
                />
                <Typography.Text>
                  {t("components.templates.settings.textTab.showText", {
                    textType: ITEM_ID_TO_INPUT_KEY[
                      text.id as keyof typeof ITEM_ID_TO_INPUT_KEY
                    ].replace("_", " "),
                  })}
                </Typography.Text>
              </Space>
            )) ?? null}
            {figure?.number && (
              <Space key={figure.number.id} align="center">
                <Toggle
                  checked={figure.number.visible}
                  onChange={(checked) => {
                    updateFigure({ visible: checked }, "number");
                    addTemplateUpdate();
                  }}
                />
                <Typography.Text>
                  {t("components.templates.settings.textTab.figureNumber")}
                </Typography.Text>
              </Space>
            )}
            {figure?.units && (
              <Space key={figure.units.id} align="center">
                <Toggle
                  checked={figure.number.visible && figure.units.visible}
                  disabled={!figure.number.visible}
                  onChange={(checked) => {
                    updateFigure({ visible: checked }, "units");
                    addTemplateUpdate();
                  }}
                />
                <Typography.Text>
                  {" "}
                  {t("components.templates.settings.textTab.figureUnits")}
                </Typography.Text>
              </Space>
            )}
          </Space>
        </PanelSection>
      )}
      {currentTextItem && (
        <>
          <PanelSection
            title={t("components.templates.settings.textTab.textStyle")}
            noBorder
          >
            <TextStyleFeature brandKitFontsOnly />
          </PanelSection>
          <PanelSection
            title={t("components.templates.settings.textTab.alignment")}
            noBorder
          >
            <ItemAlignmentFeature
              pickedItem={currentTextItem}
              onChange={(value) => {
                updateText(value);
                addTemplateUpdate();
              }}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
          </PanelSection>
          <LineHeightSection currentTextItem={currentTextItem} />
        </>
      )}
    </>
  );
};

const LineHeightSection = ({
  currentTextItem,
}: {
  currentTextItem: ParameteredText;
}) => {
  const t = useTranslate();
  const { updateParameteredText, updateFigure, parameteredTexts, figure } =
    useTemplateEditorStore((store) => ({
      updateParameteredText: store.updateParameteredText,
      updateFigure: store.updateFigure,
      parameteredTexts: store.templateState.parameteredTexts,
      figure: store.templateState.figure,
    }));
  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const lineHeightInPercentage = useMemo(
    () => Math.round((currentTextItem.lineHeight + 1) * 100),
    [currentTextItem.lineHeight]
  );

  const handleLineHeightChange = (value: number) => {
    const lineHeight = value / 100 - 1;
    if (parameteredTexts?.some((t) => t.id === currentTextItem.id)) {
      updateParameteredText({ id: currentTextItem.id, lineHeight });
    } else if (figure?.number.id === currentTextItem.id) {
      updateFigure({ lineHeight }, "number");
    } else if (figure?.units?.id === currentTextItem.id) {
      updateFigure({ lineHeight }, "units");
    }
    addTemplateUpdate();
  };

  return (
    <PanelSection
      title={t("components.templates.settings.textTab.lineHeight")}
      noBorder
    >
      <SettingSlider
        value={lineHeightInPercentage}
        onChange={handleLineHeightChange}
        step={DEFAULT_SUBTITLES_LINE_HEIGHT * 100}
        valueSuffix="%"
        max={500}
        style={{ width: "70%", marginLeft: 0 }}
        containerStyle={{ padding: 0, justifyContent: "flex-start" }}
      />
    </PanelSection>
  );
};
